import React from 'react';

import {todayCentralISO} from '../lib/dateUtils.js';
import {chipStyle, controlStyle, disabledControlStyle, fieldLabelStyle} from '../lib/formStyles.js';

// ============================================================================
// DateInput — native date field (Design Constitution I03)
// ----------------------------------------------------------------------------
// A native <input type="date"> styled per §4. The caller seeds new records with
// todayCentralISO() (today in Central time). Field forms show a 'Today' chip
// beside it (showToday). Ranges are DateRange: 'From' / 'To'.
// ============================================================================

type DateInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value: string;
  onChange: (next: string) => void;
  showToday?: boolean;
};

export default function DateInput({value, onChange, showToday = false, style, ...rest}: DateInputProps) {
  const input = (
    <input
      {...rest}
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-date-input="1"
      style={{...controlStyle, ...(showToday ? {flex: 1, minWidth: 0} : null), ...style}}
    />
  );
  if (!showToday) return input;
  const today = todayCentralISO();
  // The chip never writes what the input itself would refuse: not while the
  // field is disabled or read-only, and not when today is outside min/max.
  const min = rest.min == null ? '' : String(rest.min);
  const max = rest.max == null ? '' : String(rest.max);
  const todayBlocked =
    Boolean(rest.disabled) || Boolean(rest.readOnly) || (min !== '' && today < min) || (max !== '' && today > max);
  return (
    <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
      {input}
      <button
        type="button"
        className={value === today ? 'wcf-clickable wcf-selected' : 'wcf-clickable'}
        data-date-today="1"
        aria-pressed={value === today}
        disabled={todayBlocked}
        onClick={() => {
          if (!todayBlocked) onChange(today);
        }}
        style={{...chipStyle(value === today), flexShrink: 0, ...(todayBlocked ? disabledControlStyle : null)}}
      >
        Today
      </button>
    </div>
  );
}

type DateRangeProps = {
  from: string;
  to: string;
  onChange: (next: {from: string; to: string}) => void;
  idPrefix?: string;
  // For callers that advance the end date when the start moves past it.
  // Removing the native max lets the calendar navigate into later months.
  allowMovingStartPastEnd?: boolean;
};

export function DateRange({from, to, onChange, idPrefix, allowMovingStartPastEnd = false}: DateRangeProps) {
  const autoId = React.useId().replace(/:/g, '');
  const base = idPrefix || `range-${autoId}`;
  return (
    <div data-date-range="1" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
      <div>
        <label htmlFor={`${base}-from`} style={fieldLabelStyle}>
          From
        </label>
        <DateInput
          id={`${base}-from`}
          value={from}
          max={allowMovingStartPastEnd ? undefined : to || undefined}
          onChange={(v) => onChange({from: v, to})}
        />
      </div>
      <div>
        <label htmlFor={`${base}-to`} style={fieldLabelStyle}>
          To
        </label>
        <DateInput id={`${base}-to`} value={to} min={from || undefined} onChange={(v) => onChange({from, to: v})} />
      </div>
    </div>
  );
}
